import { PlusFillIcon, TrashIcon } from "../../shared/Icons";
import { themeAtom } from "../../shared/atoms";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import { generateUUID } from "../../shared/services/utils";
import {
  Bq1GeneralInformationM,
  IncidentDetailsM,
  initialBq1GeneralInformation,
} from "../models/bq1-general-information.model";
import { formDate } from "../services/baltic-forms.utils";
import { useAtomValue } from "jotai";
import React, { useEffect } from "react";
import { twMerge } from "tailwind-merge";

export function Bq1RecentOperationalHistory(
  form: Bq1GeneralInformationM,
  updateForm: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
  setForm: React.Dispatch<React.SetStateAction<Bq1GeneralInformationM>>,
  deleteIncident: (id: string) => void,
  readonly: boolean,
) {
  const theme = useAtomValue(themeAtom);

  useEffect(() => {
    if (form.detailsIfIncidentPast12Months.length === 0) {
      setForm((prev) => ({
        ...prev,
        detailsIfIncidentPast12Months: [
          {
            ...initialBq1GeneralInformation.detailsIfIncidentPast12Months[0],
            incidentId: generateUUID(),
          },
        ],
      }));
    }
  }, [form.detailsIfIncidentPast12Months.length, setForm]);

  const handleIncidentDetailsChange = (idx: number, field: keyof IncidentDetailsM, value: string) => {
    setForm((prev) => {
      const updated = [...prev.detailsIfIncidentPast12Months];
      (updated[idx] as any)[field] = value;
      return { ...prev, detailsIfIncidentPast12Months: updated };
    });
  };

  const addIncident = () => {
    let _incidents = [...form.detailsIfIncidentPast12Months];
    _incidents.push({
      ...initialBq1GeneralInformation.detailsIfIncidentPast12Months[0],
      incidentId: generateUUID(),
    });
    setForm((prev) => ({ ...prev, detailsIfIncidentPast12Months: _incidents }));
  };

  const removeIncident = (i: number) => {
    const id = form.detailsIfIncidentPast12Months[i].incidentId;
    deleteIncident(id);
    let _incidents = [...form.detailsIfIncidentPast12Months];
    _incidents.splice(i, 1);
    setForm((prev) => ({ ...prev, detailsIfIncidentPast12Months: _incidents }));
  };

  return (
    <div>
      <h2 className="mb-2 mt-[120px] text-lg font-semibold">Recent Operational History</h2>
      <br />
      {/* Involved in incident */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-bold">
            Has the ship been involved in a pollution, grounding, collision or allision incident during the past 12
            months?:
          </label>
          <div className="mt-1 flex space-x-4">
            {["yes", "no"].map((val) => (
              <label key={val} className="inline-flex items-center space-x-1">
                <input
                  disabled={readonly}
                  type="radio"
                  name="pollutionGroundingCollision12Months"
                  value={val}
                  checked={form.pollutionGroundingCollision12Months === val}
                  onChange={updateForm}
                  className="form-radio"
                />
                <span className="text-sm capitalize">{val}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* If yes */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-4">
          <label className="block text-sm font-bold">
            If yes, vessel has been involved in a pollution, grounding, collision or allision incident during the past
            12 months, provide details:
          </label>
        </div>
      </div>
      <div className={twMerge("mb-4 flex gap-2 border-b pb-2 text-xs font-bold", theme.uwhcAssistant.inputBorder)}>
        <div className="w-[2%]">
          <label>#</label>
        </div>
        <div className="w-[20%]">
          <label className="mb-1 block text-xs font-bold">Date</label>
        </div>
        <div className="w-[20%]">
          <label className="mb-1 block text-xs font-bold">Type of incident</label>
        </div>
        <div className="w-[20%]">
          <label className="mb-1 block text-xs font-bold">Geographical Location</label>
        </div>
        <div className="w-[2%]"></div>
      </div>
      {form.detailsIfIncidentPast12Months?.map((incident, i) => (
        <div key={i} className="mb-4 flex items-center gap-2">
          <div className="w-[2%]">{i + 1}</div>
          <div className="w-[20%]">
            <Datepicker
              selected={formDate(incident.date ?? "")}
              onChange={(date) => handleIncidentDetailsChange(i, "date", date.toISOString())}
            />
          </div>

          <div className="w-[20%]">
            <select
              name="typeOfIncident"
              value={incident.type}
              onChange={(e) => handleIncidentDetailsChange(i, "type", e.target.value)}
              className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
            >
              <option value=""></option>
              <option value="Pollution">Pollution</option>
              <option value="Grounding">Grounding</option>
              <option value="Collision">Collision</option>
              <option value="Allision">Allision</option>
            </select>
          </div>

          <div className="w-[20%]">
            <input
              type="text"
              value={incident.location}
              onChange={(e) => handleIncidentDetailsChange(i, "location", e.target.value)}
              className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
            />
          </div>
          {!readonly && (
            <div className="w-[2%]">
              <button className="mt-2 text-xs text-red-500" onClick={() => removeIncident(i)}>
                <TrashIcon width={22} height={22} />
              </button>
            </div>
          )}
        </div>
      ))}

      {!readonly && (
        <button className="mb-8 inline-flex items-center rounded py-2 text-green" onClick={addIncident}>
          <PlusFillIcon className="inline-block h-6 w-6 pr-1" />
          <span className="text-sm hover:underline focus:outline-none">Add New Incident</span>
        </button>
      )}

      {/* Last three voyages */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-bold">Use answer from Cargo History:</label>
          <div className="mb-6 mt-1 flex space-x-4">
            {["Short answer - cargo names", "Full answer - from Cargo History", "I will write it myself"].map((val) => (
              <label key={val} className="inline-flex items-center space-x-1">
                <input
                  disabled={readonly}
                  type="radio"
                  name="useAnswerFromCargoHistory"
                  value={val}
                  checked={form.useAnswerFromCargoHistory === val}
                  onChange={updateForm}
                  className="form-radio"
                />
                <span className="text-sm capitalize">{val}</span>
              </label>
            ))}
          </div>
          <p className="text-sm">Last three cargoes/charterers/voyages (Last/2nd Last/3rd Last)</p>
          <textarea
            disabled={readonly}
            name="lastThreeVoyages"
            value={form.lastThreeVoyages}
            onChange={updateForm}
            className="mt-1 h-20 w-full resize-none rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>
      </div>

      {/* International Ship Security Certificate (ISSC) */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-bold">International Ship Security Certificate (ISSC):</label>
          <p>(Security code the vessel is currently operating under)</p>
          <select
            disabled={readonly}
            name="internationalShipSecurityCertificate"
            value={form.internationalShipSecurityCertificate}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          >
            <option value=""></option>
            <option value="Level 1">Level 1</option>
            <option value="Level 2">Level 2</option>
            <option value="Level 3">Level 3</option>
          </select>
        </div>
      </div>
    </div>
  );
}
